import { resolve } from 'path';
import dotenv from 'dotenv';
import { webEnvSchema, mobileEnvSchema } from './validation';
import type { NextEnv, ExpoEnv, ValidatedEnv, UnifiedEnv } from './types';

// Determine if we're in Expo
const isExpo = typeof process !== 'undefined' && 
  (process.env.EXPO_PUBLIC_DEBUG !== undefined || process.env.EXPO_APP_ENV !== undefined);

// Platform identifier
const PLATFORM = isExpo ? 'Expo' : 'Next.js';

// Development mode detection
const isDevelopment = process.env.NODE_ENV === 'development' || 
  process.env.NEXT_PUBLIC_APP_ENV === 'development' || 
  process.env.EXPO_PUBLIC_APP_ENV === 'development';

// Debug logger
const debugLog = (message: string, data?: any) => {
  try {
    if (process.env.DEBUG === 'true' || process.env.NEXT_PUBLIC_DEBUG === 'true') {
      console.log(`[ENV] ${message}`, data || '');
    }
  } catch (e) {
    // Silently fail if console.log is not available
  }
};

// Load environment from file
const loadEnvFile = (filePath: string) => {
  try {
    const result = dotenv.config({ path: filePath });
    if (result.error) {
      debugLog(`Error loading .env file: ${filePath}`, { error: result.error.message });
      return null;
    }
    debugLog(`Loaded environment from: ${filePath}`);
    return result.parsed || null;
  } catch (e) {
    debugLog(`Exception loading .env file: ${filePath}`);
    return null;
  }
};

// Get raw environment variables with fallbacks for development
const getRawEnv = (): Record<string, string | undefined> => {
  // Try to load from .env files in development
  let loadedEnv = {};
  
  if (isDevelopment) {
    // Try multiple possible paths
    const paths = [
      '.env.development',
      '.env',
      '../.env.development',
      '../.env',
    ];
    
    for (const path of paths) {
      try {
        const resolvedPath = resolve(process.cwd(), path);
        const envFile = loadEnvFile(resolvedPath);
        if (envFile) {
          debugLog(`Loaded environment from ${resolvedPath}`);
          loadedEnv = { ...loadedEnv, ...envFile };
          break;
        }
      } catch (e) {
        // Continue to next path
      }
    }
  }
  
  // Return environment with fallbacks
  if (!isExpo) {
    const env = {
      // Combine process.env with loaded values, prioritizing process.env
      NEXT_PUBLIC_SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL || (loadedEnv as any).NEXT_PUBLIC_SUPABASE_URL,
      NEXT_PUBLIC_SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || (loadedEnv as any).NEXT_PUBLIC_SUPABASE_ANON_KEY,
      NEXT_PUBLIC_APP_URL: process.env.NEXT_PUBLIC_APP_URL || (loadedEnv as any).NEXT_PUBLIC_APP_URL,
      NEXT_PUBLIC_APP_ENV: process.env.NEXT_PUBLIC_APP_ENV || (loadedEnv as any).NEXT_PUBLIC_APP_ENV || 'development',
      NEXT_PUBLIC_DEBUG: process.env.NEXT_PUBLIC_DEBUG || (loadedEnv as any).NEXT_PUBLIC_DEBUG || 'false',
      NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY || (loadedEnv as any).NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
      DATABASE_URL: process.env.DATABASE_URL || (loadedEnv as any).DATABASE_URL,
      SUPABASE_SERVICE_ROLE_KEY: process.env.SUPABASE_SERVICE_ROLE_KEY || (loadedEnv as any).SUPABASE_SERVICE_ROLE_KEY,
      STRIPE_SECRET_KEY: process.env.STRIPE_SECRET_KEY || (loadedEnv as any).STRIPE_SECRET_KEY,
      STRIPE_WEBHOOK_SECRET: process.env.STRIPE_WEBHOOK_SECRET || (loadedEnv as any).STRIPE_WEBHOOK_SECRET,
      SESSION_SECRET: process.env.SESSION_SECRET || (loadedEnv as any).SESSION_SECRET,
      PORT: process.env.PORT || (loadedEnv as any).PORT,
    };
    
    return env;
  }
  
  // Expo environment
  return {
    EXPO_PUBLIC_SUPABASE_URL: process.env.EXPO_PUBLIC_SUPABASE_URL || (loadedEnv as any).EXPO_PUBLIC_SUPABASE_URL,
    EXPO_PUBLIC_SUPABASE_ANON_KEY: process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY || (loadedEnv as any).EXPO_PUBLIC_SUPABASE_ANON_KEY,
    EXPO_PUBLIC_APP_URL: process.env.EXPO_PUBLIC_APP_URL || (loadedEnv as any).EXPO_PUBLIC_APP_URL,
    EXPO_PUBLIC_APP_ENV: process.env.EXPO_PUBLIC_APP_ENV || (loadedEnv as any).EXPO_PUBLIC_APP_ENV || 'development',
    EXPO_PUBLIC_DEBUG: process.env.EXPO_PUBLIC_DEBUG || (loadedEnv as any).EXPO_PUBLIC_DEBUG || 'false',
    EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY: process.env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY || (loadedEnv as any).EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY,
  };
};

// Local validation function (not imported from validation.ts)
const localValidateEnv = (env: Record<string, any>): ValidatedEnv => {
  try {
    if (isDevelopment) {
      debugLog('Using relaxed validation for development environment');
      
      // In development, use partial validation to allow the build to proceed
      if (!isExpo) {
        return webEnvSchema.partial().parse(env) as ValidatedEnv;
      }
      return mobileEnvSchema.partial().parse(env) as ValidatedEnv;
    }

    // In production, use strict validation
    if (!isExpo) {
      return webEnvSchema.parse(env);
    }
    return mobileEnvSchema.parse(env);
  } catch (error) {
    debugLog('Environment validation error', { error });
    
    // In development, return the environment as-is without validation
    if (isDevelopment) {
      debugLog('Bypassing validation in development');
      return env as ValidatedEnv;
    }
    
    throw error;
  }
};

// Create the environment object
const createEnv = (): UnifiedEnv => {
  const rawEnv = getRawEnv();
  debugLog('Raw environment variables', { keys: Object.keys(rawEnv) });
  
  const validatedEnv = localValidateEnv(rawEnv);
  debugLog('Validated environment variables', { keys: Object.keys(validatedEnv) });

  if (!isExpo) {
    const env = validatedEnv as NextEnv;
    
    // Create unified environment for Next.js
    return {
      supabase: {
        url: env.NEXT_PUBLIC_SUPABASE_URL || '',
        anonKey: env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '',
        serviceRoleKey: env.SUPABASE_SERVICE_ROLE_KEY,
      },
      app: {
        url: env.NEXT_PUBLIC_APP_URL || '',
        environment: (env.NEXT_PUBLIC_APP_ENV || 'development') as 'development' | 'staging' | 'production',
        debug: typeof env.NEXT_PUBLIC_DEBUG === 'string' ? env.NEXT_PUBLIC_DEBUG === 'true' : !!env.NEXT_PUBLIC_DEBUG,
        port: process.env.PORT ? parseInt(process.env.PORT, 10) : undefined,
      },
      database: {
        url: env.DATABASE_URL,
        ssl: process.env.NODE_ENV === 'production',
      },
      auth: {
        sessionSecret: env.SESSION_SECRET,
        tokenExpiry: 7 * 24 * 60 * 60, // 7 days in seconds
      },
      stripe: {
        publishableKey: env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
        secretKey: env.STRIPE_SECRET_KEY,
        webhookSecret: env.STRIPE_WEBHOOK_SECRET,
      },
    };
  }

  // Create unified environment for Expo
  const env = validatedEnv as ExpoEnv;
  return {
    supabase: {
      url: env.EXPO_PUBLIC_SUPABASE_URL || '',
      anonKey: env.EXPO_PUBLIC_SUPABASE_ANON_KEY || '',
    },
    app: {
      url: env.EXPO_PUBLIC_APP_URL || '',
      environment: (env.EXPO_PUBLIC_APP_ENV || 'development') as 'development' | 'staging' | 'production',
      debug: typeof env.EXPO_PUBLIC_DEBUG === 'string' ? env.EXPO_PUBLIC_DEBUG === 'true' : !!env.EXPO_PUBLIC_DEBUG,
    },
    stripe: {
      publishableKey: env.EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY,
    },
  };
};

// Export the environment
export const env = createEnv(); 